import { useMemo } from 'react';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import type { EditModalProps } from './editModal';

type UseEditModalArgs = Pick<
  EditModalProps,
  | 'isMultiStep'
  | 'inputList'
  | 'isOpen'
  | 'onClickSave'
  | 'toggle'
  | 'maxChar'
  | 'header'
  | 'onChange'
>;

const useEditModal = ({
  isMultiStep = false,
  inputList,
  isOpen,
  onClickSave,
  toggle,
  maxChar,
  header,
  onChange = () => {},
}: UseEditModalArgs) => {
  const [state, setState] = useMergeState({
    step: 0,
  });

  const isDifferent = useMemo(
    () => isMultiStep && inputList[0][0].isDifferent && state.step !== 1,
    [isMultiStep, inputList, state.step],
  );

  /**
   * Returns a multi-step edit modal to its previous step. Decrements the step counter held in local state.
   *
   * @returns Nothing.
   */
  const onClickBack = () => {
    setState({ step: state.step - 1 });
  };

  /**
   * Decides whether the modal's save button advances a step or commits the edit. In multi-step mode it moves to the next step while the current step is not the last and the values differ, otherwise it calls the supplied onClickSave to commit.
   *
   * @returns Nothing.
   */
  const handleClickSave = () => {
    if (isMultiStep) {
      if (state.step !== 1 && isDifferent) {
        setState({ step: state.step + 1 });
      } else {
        onClickSave();
      }
    } else {
      onClickSave();
    }
  };

  /**
   * Dismisses the edit modal from its close button. Invokes the toggle callback supplied by the owning drawer.
   *
   * @returns Nothing.
   */
  const onClickCloseButton = () => {
    toggle();
  };

  /**
   * Guards an edit-modal field change against the field's character limit before letting it through. When a maximum is configured and the new value is longer, it raises a toast naming the limit and the modal header and drops the change, otherwise it forwards name, value and any extra payload to onChange.
   *
   * @param name - Name of the field being edited.
   * @param value - New value entered for the field.
   * @param extra - Optional extra payload forwarded with the change.
   * @returns Nothing.
   */
  const onChangeField = (name: string, value: any, extra?: any) => {
    if (maxChar && value?.length > maxChar) {
      toastrError(`Exceeded maximum of ${maxChar} characters in ${header}`, 'Error');
      return;
    }
    onChange(name, value, extra);
  };

  /**
   * Relays a report due-date pick from the date picker into the modal's change handler. Passes the field name and the picked date value through to onChange, dropping the formatted date string the picker also supplies.
   *
   * @param name - Name of the due-date field being changed.
   * @param date - Date value chosen in the picker.
   * @param dateString - Formatted text of the chosen date; unused.
   * @returns Nothing.
   */
  const onChangeReportDueDate = (name: string, date: any, dateString?: string) => {
    onChange(name, date);
  };

  useUpdateEffect(() => {
    if (isOpen && isMultiStep) {
      setState({ step: 0 });
    }
  }, [isOpen]);

  return {
    state,
    isDifferent,
    onClickBack,
    handleClickSave,
    onClickCloseButton,
    onChangeField,
    onChangeReportDueDate,
  };
};

export default useEditModal;
